import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { Link } from 'react-router-dom';

interface Provider {
  id: string;
  business_name: string;
  trade_type: string;
  rating: number;
  total_jobs: number;
  user_id: string;
}

interface User {
  full_name: string;
  phone: string;
}

interface Favorite {
  id: string;
  provider_id: string;
  added_at: string;
  notes: string | null;
  provider: Provider;
  user: User;
  lastReview?: {
    rating: number;
    comment: string;
    created_at: string;
  };
}

export default function FavoriteProviders() {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Favorite | null>(null);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Please sign in to view favorites');
        setLoading(false);
        return;
      }

      // Step 1: Get favorite provider IDs
      const { data: favoriteData, error: favError } = await supabase
        .from('favorite_providers')
        .select('id, provider_id, added_at, notes')
        .eq('user_id', session.user.id)
        .order('added_at', { ascending: false });

      if (favError) {
        console.error('Error fetching favorites:', favError);
        setError('Failed to load favorites. Please try again.');
        setLoading(false);
        return;
      }

      if (!favoriteData || favoriteData.length === 0) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      // Step 2: Get provider profiles
      const providerIds = favoriteData.map(f => f.provider_id);
      const { data: providers, error: provError } = await supabase
        .from('provider_profiles')
        .select('id, business_name, trade_type, rating, total_jobs, user_id')
        .in('id', providerIds);

      if (provError) {
        console.error('Error fetching providers:', provError);
        setError('Failed to load provider details.');
        setLoading(false);
        return;
      }

      // Step 3: Get user details for providers
      const userIds = providers?.map(p => p.user_id) || [];
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('id, full_name, phone')
        .in('id', userIds);

      if (userError) {
        console.error('Error fetching users:', userError);
      }

      // Step 4: Get reviews
      const { data: reviews } = await supabase
        .from('reviews')
        .select('provider_id, rating, comment, created_at')
        .eq('user_id', session.user.id)
        .in('provider_id', providerIds)
        .order('created_at', { ascending: false });

      // Step 5: Combine all data
      const combinedFavorites: Favorite[] = favoriteData.map(fav => {
        const provider = providers?.find(p => p.id === fav.provider_id);
        const user = users?.find(u => u.id === provider?.user_id);
        const lastReview = reviews?.find(r => r.provider_id === fav.provider_id);

        return {
          id: fav.id,
          provider_id: fav.provider_id,
          added_at: fav.added_at,
          notes: fav.notes,
          provider: provider || {
            id: fav.provider_id,
            business_name: 'Unknown Provider',
            trade_type: 'General',
            rating: 0,
            total_jobs: 0,
            user_id: ''
          },
          user: user || {
            full_name: 'Unknown',
            phone: ''
          },
          lastReview: lastReview ? {
            rating: lastReview.rating,
            comment: lastReview.comment,
            created_at: lastReview.created_at
          } : undefined
        };
      });

      setFavorites(combinedFavorites);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId: string, providerId: string) => {
    try {
      setRemovingId(providerId);
      
      const { error } = await supabase
        .from('favorite_providers')
        .delete()
        .eq('id', favoriteId);

      if (error) {
        console.error('Error removing favorite:', error);
        alert('Failed to remove favorite. Please try again.');
        return;
      }

      setFavorites(favorites.filter(f => f.id !== favoriteId));
    } catch (error) {
      console.error('Error removing favorite:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="ri-error-warning-line text-6xl text-red-300 mb-4"></i>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Favorites</h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={loadFavorites}
          className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Try Again
        </button>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Favorite Providers Yet</h3>
        <p className="text-gray-600 mb-6">
          After completing a job, you can add providers you like to your favorites for easy rebooking.
        </p>
        <Link
          to="/services"
          className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-search-line"></i>
          Browse Services
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Favorite Providers</h2>
        <p className="text-gray-600">Your trusted service providers for quick rebooking</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {favorites.map((favorite) => (
          <div
            key={favorite.id}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {favorite.provider.business_name}
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  {favorite.provider.trade_type}
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400 text-sm"></i>
                    <span className="text-sm font-medium text-gray-900">
                      {favorite.provider.rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600">
                    {favorite.provider.total_jobs} jobs
                  </span>
                </div>
              </div>
              <button
                onClick={() => removeFavorite(favorite.id, favorite.provider_id)}
                disabled={removingId === favorite.provider_id}
                className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title="Remove from favorites"
              >
                {removingId === favorite.provider_id ? (
                  <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <i className="ri-heart-fill text-xl"></i>
                )}
              </button>
            </div>

            {favorite.notes && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 italic">&quot;{favorite.notes}&quot;</p>
              </div>
            )}

            {favorite.lastReview && (
              <div className="mb-4 p-3 bg-teal-50 rounded-lg">
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-fill text-xs ${
                        i < favorite.lastReview!.rating ? 'text-yellow-400' : 'text-gray-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-xs text-gray-700 line-clamp-2">{favorite.lastReview.comment}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setSelectedProvider(favorite)}
                className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Book Again
              </button>
              {favorite.user.phone && (
                <a
                  href={`tel:${favorite.user.phone}`}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Call provider"
                >
                  <i className="ri-phone-line"></i>
                </a>
              )}
            </div>

            <div className="mt-3 text-xs text-gray-500">
              Added {new Date(favorite.added_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {selectedProvider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Request Service</h3>
              <button
                onClick={() => setSelectedProvider(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {selectedProvider.provider.business_name}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedProvider.provider.trade_type}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                You'll be redirected to the service request page where you can specify your requirements and schedule a booking with this provider.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedProvider(null)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
              >
                Cancel
              </button>
              <Link
                to={`/service-request?provider=${selectedProvider.provider_id}&trade=${selectedProvider.provider.trade_type}`}
                className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium text-center whitespace-nowrap"
              >
                Continue
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
